import React from 'react';
import '../../base.scss';
import './Autocomplete.scss';
import { AutocompleteSuggestion } from './AutocompleteSuggestion';
import { classNames } from '../../utilities';

export interface AutocompleteDropdownProps {
  /** CSS class name applied */
  className?: string;
  /** Index of a suggestion that should have focus */
  focusedSuggestion?: number;
  /** Index of a suggestion which should have highlight styling */
  highlightedSuggestion?: number;
  /** Whether this dropdown appears */
  isOpen?: boolean;
  /** Whether the dropdown is in loading state */
  isLoading?: boolean;
  /** A message to display in the dropdown */
  message?: string;
  /** Callback when a selection is made */
  onSelectSuggestion: (index: number) => void;
  /** Callback when a suggestion is focused by the user */
  onFocusSuggestion: (index: number) => void;
  /** List of suggestions to display */
  suggestions?: string[];
  /** Label to show above suggestions to provide context to the user */
  suggestionsHint?: string;
}

/**
 * Dropdown portion of the Autocomplete component, split out for
 * easier reusability across Autocomplete variants. 
 */
export function AutocompleteDropdown(props: AutocompleteDropdownProps) {
  const {
    className,
    focusedSuggestion = -1,
    highlightedSuggestion,
    isOpen,
    isLoading,
    message,
    suggestions,
    suggestionsHint,
    onSelectSuggestion,
    onFocusSuggestion,
  } = props;

  const hasSuggestions = suggestions && suggestions.length > 0;

  const suggestionList = suggestions?.map((text, i) => (
    <AutocompleteSuggestion 
      onClick={onSelectSuggestion}
      onFocus={onFocusSuggestion} // keep our focus synchronized
      key={`ps-suggestion-${i}-${text}`}
      isFocused={focusedSuggestion === i ? true : false}
      isHighlighted={highlightedSuggestion !== undefined && i === highlightedSuggestion}
      index={i}
      value={text}
    >
      {text}
    </AutocompleteSuggestion>
  ));

  const dropdownClasses = classNames(
    'ps-autocomplete__dropdown',
    !isOpen || (!hasSuggestions && !message) ? ' ps-autocomplete__dropdown--hidden ' : '',
    className
  );

  return (
    <div 
      className={dropdownClasses}
    >
      <div className="ps-autocomplete__dropdown__content">
        <div className="ps-autocomplete__suggestions">
          {message && (
            <div className="ps-autocomplete__message">
              {message}
            </div>
          )}
          {!isLoading && hasSuggestions && (
            <>
              {suggestionsHint && <p className="ps-autocomplete__message--small">{suggestionsHint}</p>}
              {suggestionList}
            </>
          )}
        </div>
      </div>
    </div>
  );
}